<template>
    <!-- Modal wrapper - only show when not embedded -->
    <div v-if="show && !embedded"
    class="fixed inset-0 bg-gray-900 bg-opacity-75 flex justify-center items-center z-50 backdrop-blur-sm">
        <div class="bg-white rounded-lg shadow-lg w-8/12 max-h-[90vh] flex flex-col">
            <!-- Header -->
            <div class="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 class="text-2xl font-bold">Add Assessment</h2>
                <button @click="closeModal" class="text-gray-500 hover:text-gray-700" aria-label="Close">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12">
                        </path>
                    </svg>
                </button>
            </div>

            <!-- Form content -->
            <div class="overflow-y-auto flex-1 px-8">
                <div class="py-6">
                    <form @submit.prevent="submitForm">
                        <div class="grid grid-cols-2 gap-6">
                            <!-- Left Column -->
                            <div class="space-y-4">
                                <div>
                                    <label for="assessmentName" class="block font-medium mb-2">Test Name<span
                                            class="text-red-500">*</span></label>
                                    <input id="assessmentName" v-model="formData.assessment_name" type="text"
                                        class="border border-gray-300 px-3 py-2 rounded-md w-full" required />
                                </div>

                                <!-- Enhanced Search Select for Basis -->
                                <div>
                                    <label for="basis" class="block font-medium mb-2">
                                        Basis Documents<span class="text-red-500">*</span>
                                        <span class="text-xs text-gray-500 ml-1">(Select training materials this assessment is based on)</span>
                                    </label>
                                    <div class="relative basis-select">
                                        <!-- Search Input with Selected Items -->
                                        <div
                                            class="relative flex flex-wrap items-center gap-2 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-colors">
                                            <!-- Selected Documents Display -->
                                            <span v-for="docId in formData.basis" :key="docId"
                                                class="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center text-sm font-medium">
                                                <span class="text-xs text-blue-600 mr-2 font-semibold">[{{ getDocumentType(docId)
                                                    }}]</span>
                                                {{ getDocumentName(docId) }}
                                                <button type="button" @click.stop="removeBasis(docId)"
                                                    class="ml-2 text-blue-600 hover:text-blue-800 focus:outline-none">
                                                    <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                                                    </svg>
                                                </button>
                                            </span>
                                            <!-- Search Input -->
                                            <input type="text" v-model="basisSearch" @input="filterDocuments"
                                                @focus="handleSearchFocus" @keydown.escape="showBasisDropdown = false"
                                                placeholder="Type to search documents..." 
                                                class="flex-grow border-none focus:ring-0 outline-none placeholder-gray-400 text-sm py-1" />
                                        </div>

                                        <!-- Dropdown Container -->
                                        <div v-if="showBasisDropdown"
                                            class="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl">
                                            <!-- Header with search info -->
                                            <div class="px-4 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                                                <div class="flex items-center justify-between">
                                                    <span class="text-sm font-medium text-gray-700">Select Documents</span>
                                                    <span class="text-xs text-gray-500">{{ filteredDocuments.length }} found</span>
                                                </div>
                                            </div>
                                            
                                            <!-- Type Tabs -->
                                            <div class="flex border-b border-gray-200 bg-gray-50">
                                                <button @click="selectedDocumentType = ''" :class="[
                                                    'px-4 py-2 text-sm font-medium transition-colors',
                                                    !selectedDocumentType ?
                                                        'text-blue-600 border-b-2 border-blue-600 bg-white' :
                                                        'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                                ]">
                                                    All Types
                                                </button>
                                                <button v-for="type in documentTypes" :key="type"
                                                    @click="selectedDocumentType = type" :class="[
                                                        'px-4 py-2 text-sm font-medium transition-colors',
                                                        selectedDocumentType === type ?
                                                            'text-blue-600 border-b-2 border-blue-600 bg-white' :
                                                            'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                                    ]">
                                                    {{ type }}
                                                </button>
                                            </div>

                                            <!-- Results Section -->
                                            <div class="max-h-60 overflow-y-auto">
                                                <div v-if="filteredDocuments.length > 0">
                                                    <div v-for="doc in filteredDocuments" :key="doc.document_id"
                                                        @click="selectBasis(doc)"
                                                        class="px-4 py-3 hover:bg-blue-50 cursor-pointer flex items-center border-b border-gray-100 last:border-b-0 transition-colors">
                                                        <div class="flex-1">
                                                            <div class="flex items-center">
                                                                <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-3">
                                                                    {{ doc.document_type }}
                                                                </span>
                                                                <span class="font-medium text-gray-900">{{ doc.document_name }}</span>
                                                            </div>
                                                            <div class="text-xs text-gray-500 mt-1" v-if="doc.description">
                                                                {{ doc.description.substring(0, 60) }}{{ doc.description.length > 60 ? '...' : '' }}
                                                            </div>
                                                        </div>
                                                        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                                                        </svg>
                                                    </div>
                                                </div>
                                                <div v-else class="p-6 text-center">
                                                    <svg class="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.709"></path>
                                                    </svg>
                                                    <p class="text-gray-500 text-sm">No documents found</p>
                                                    <p class="text-gray-400 text-xs mt-1">Try adjusting your search or filter</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>



                                <!-- Enhanced Department Multi-Select -->
                                <div v-if="showDepartmentField" class="mb-4">
                                    <label for="department" class="block font-medium mb-2">
                                        Departments<span class="text-red-500">*</span>
                                        <span class="text-xs text-gray-500 ml-1">(Select which departments this assessment applies to)</span>
                                    </label>
                                    <div class="relative">
                                        <!-- Selected Departments -->
                                        <div class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-colors"
                                            @click="toggleDepartmentDropdown">
                                            <div class="flex flex-wrap gap-2">
                                                <span v-for="(department, index) in formData.department_type"
                                                    :key="department"
                                                    class="bg-green-100 text-green-800 px-3 py-1 rounded-full flex items-center text-sm font-medium">
                                                    {{ getDepartmentName(department) }}
                                                    <button type="button" @click.stop="removeDepartment(index)"
                                                        class="ml-2 text-green-600 hover:text-green-800 focus:outline-none">
                                                        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                                                        </svg>
                                                    </button>
                                                </span>
                                            </div>
                                            <div v-if="!formData.department_type.length" class="text-gray-400 text-sm py-1">
                                                Click to select departments
                                            </div>
                                        </div>
                                        <div
                                            class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                            <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20">
                                                <path
                                                    d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                            </svg>
                                        </div>

                                        <!-- Enhanced Dropdown -->
                                        <div v-if="isDepartmentDropdownVisible"
                                            class="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl">
                                            <!-- Header -->
                                            <div class="px-4 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                                                <div class="flex items-center justify-between">
                                                    <span class="text-sm font-medium text-gray-700">Select Departments</span>
                                                    <span class="text-xs text-gray-500">{{ departments.length }} available</span>
                                                </div>
                                            </div>
                                            
                                            <!-- Results Section -->
                                            <div class="max-h-60 overflow-y-auto">
                                                <div v-for="department in departments" :key="department.department_id"
                                                    @click="toggleDepartment(department.department_id)"
                                                    class="px-4 py-3 hover:bg-green-50 cursor-pointer flex items-center border-b border-gray-100 last:border-b-0 transition-colors">
                                                    <div class="flex items-center flex-1">
                                                        <input type="checkbox" :checked="formData.department_type.includes(department.department_id)"
                                                            class="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 mr-3" />
                                                        <div class="flex-1">
                                                            <span class="font-medium text-gray-900">{{ department.name }}</span>
                                                            <div class="text-xs text-gray-500 mt-1" v-if="department.description">
                                                                {{ department.description.substring(0, 50) }}{{ department.description.length > 50 ? '...' : '' }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4"></path>
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Enhanced User Type Multi-Select -->
                                <div v-if="showUserTypeField" class="mb-4">
                                    <label for="userType" class="block font-medium mb-2">
                                        User Types<span class="text-red-500">*</span>
                                        <span class="text-xs text-gray-500 ml-1">(Select which user types can take this assessment)</span>
                                    </label>
                                    <div class="relative">
                                        <!-- Selected Roles -->
                                        <div class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-colors"
                                            @click="toggleUserTypeDropdown">
                                            <div class="flex flex-wrap gap-2">
                                                <span v-for="(role, index) in formData.user_type" :key="role"
                                                    class="bg-purple-100 text-purple-800 px-3 py-1 rounded-full flex items-center text-sm font-medium">
                                                    {{ getRoleName(role) }}
                                                    <button type="button" @click.stop="removeRole(index)"
                                                        class="ml-2 text-purple-600 hover:text-purple-800 focus:outline-none">
                                                        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                                                        </svg>
                                                    </button>
                                                </span>
                                            </div>
                                            <div v-if="!formData.user_type.length" class="text-gray-400 text-sm py-1">
                                                Click to select user types
                                            </div>
                                        </div>
                                        <div
                                            class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                            <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20">
                                                <path
                                                    d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                            </svg>
                                        </div>

                                        <!-- Enhanced Dropdown -->
                                        <div v-if="isUserTypeDropdownVisible"
                                            class="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl">
                                            <!-- Header -->
                                            <div class="px-4 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                                                <div class="flex items-center justify-between">
                                                    <span class="text-sm font-medium text-gray-700">Select User Types</span>
                                                    <span class="text-xs text-gray-500">{{ roles.length }} available</span>
                                                </div>
                                            </div>
                                            
                                            <!-- Results Section -->
                                            <div class="max-h-60 overflow-y-auto">
                                                <div v-for="role in roles" :key="role.role_id"
                                                    @click="toggleRole(role.role_id)"
                                                    class="px-4 py-3 hover:bg-purple-50 cursor-pointer flex items-center border-b border-gray-100 last:border-b-0 transition-colors">
                                                    <div class="flex items-center flex-1">
                                                        <input type="checkbox" :checked="formData.user_type.includes(role.role_id)"
                                                            class="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 mr-3" />
                                                        <div class="flex-1">
                                                            <span class="font-medium text-gray-900">{{ role.name }}</span>
                                                            <div class="text-xs text-gray-500 mt-1" v-if="role.description">
                                                                {{ role.description.substring(0, 50) }}{{ role.description.length > 50 ? '...' : '' }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4"></path>
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label for="deadline" class="block font-medium mb-2">Deadline<span
                                            class="text-red-500">*</span></label>
                                    <input id="deadline" v-model="formData.deadline" type="datetime-local"
                                        class="border border-gray-300 px-3 py-2 rounded-md w-full" required />
                                </div>
                            </div>

                            <!-- Right Column -->
                            <div class="space-y-4">
                                <div>
                                    <label for="numberOfQuestions" class="block font-medium mb-2">Number of
                                        Questions<span class="text-red-500">*</span></label>
                                    <input id="numberOfQuestions" v-model="formData.number_of_questions" type="number"
                                        class="border border-gray-300 px-3 py-2 rounded-md w-full" required />
                                </div>

                                <div>
                                    <label for="passmark" class="block font-medium mb-2">Passmark (%)<span
                                            class="text-red-500">*</span></label>
                                    <input id="passmark" v-model="formData.passmark" type="number" min="0" max="100"
                                        class="border border-gray-300 px-3 py-2 rounded-md w-full" required />
                                </div>

                                <div>
                                    <label for="cpdAccredited" class="block font-medium mb-2">CPD Accredited<span
                                            class="text-red-500">*</span></label>
                                    <select id="cpdAccredited" v-model="formData.cpd_accredited"
                                        class="border border-gray-300 px-3 py-2 rounded-md w-full" required>
                                        <option value="">Select option</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </select>
                                </div>

                                <!-- Certificate expiry period (days) shown only if CPD Accredited is Yes) -->
                                <div v-if="String(formData.cpd_accredited).toLowerCase() === 'yes'">
                                    <label class="block font-medium mb-2">Certificate Expiry Period (days)</label>
                                    <input v-model.number="formData.certificate_expiry_period" type="number" min="0"
                                        class="border border-gray-300 px-3 py-2 rounded-md w-full" placeholder="e.g. 365" />
                                </div>

                                

                                <!-- Duration Selection Field -->
                                <div class="relative">
                                    <label for="timeSelection" class="block font-medium mb-2">Duration<span
                                            class="text-red-500">*</span></label>
                                    <div class="flex items-center space-x-2">
                                        <!-- Hours -->
                                        <div class="relative flex flex-col items-center">
                                            <span class="text-gray-400 text-sm">Hours</span>
                                            <select v-model="hours" @change="updateDuration"
                                                class="bg-white border border-gray-300 rounded-md px-4 py-2 appearance-none w-20 text-center focus:outline-none focus:ring-2 focus:ring-blue-500">
                                                <option v-for="n in 100" :key="n - 1" :value="n - 1">
                                                    {{ String(n - 1).padStart(2, '0') }}
                                                </option>
                                            </select>
                                        </div>

                                        <!-- Colon separator -->
                                        <div class="text-xl font-medium">:</div>

                                        <!-- Minutes -->
                                        <div class="relative flex flex-col items-center">
                                            <span class="text-gray-400 text-sm">Minutes</span>
                                            <select v-model="minutes" @change="updateDuration"
                                                class="bg-white border border-gray-300 rounded-md px-4 py-2 appearance-none w-20 text-center focus:outline-none focus:ring-2 focus:ring-blue-500">
                                                <option v-for="n in 60" :key="n - 1" :value="n - 1">
                                                    {{ String(n - 1).padStart(2, '0') }}
                                                </option>
                                            </select>
                                        </div>

                                        <!-- Colon separator -->
                                        <div class="text-xl font-medium">:</div>

                                        <!-- Seconds -->
                                        <div class="relative flex flex-col items-center">
                                            <span class="text-gray-400 text-sm">Seconds</span>
                                            <select v-model="seconds" @change="updateDuration"
                                                class="bg-white border border-gray-300 rounded-md px-2 py-2 appearance-none w-20 text-center focus:outline-none focus:ring-2 focus:ring-blue-500">
                                                <option v-for="n in 60" :key="n - 1" :value="n - 1">
                                                    {{ String(n - 1).padStart(2, '0') }}
                                                </option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>

                        <!-- Description field -->
                        <div class="mt-6">
                            <label for="description" class="block font-medium mb-2">Description<span
                                    class="text-red-500">*</span></label>
                            <textarea id="description" v-model="formData.description"
                                class="border border-gray-300 px-3 py-2 rounded-md w-full" rows="3" required></textarea>
                        </div>
                    </form>
                </div>
            </div>

            <!-- Footer -->
            <div class="border-t border-gray-200 p-4 px-8">
                <div class="flex justify-end">
                    <button type="button" @click="clearForm"
                        class="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-md mr-2">
                        Cancel
                    </button>
                    <button @click="submitForm" :disabled="isSubmitting"
                        class="bg-custom-yellow hover:bg-yellow-500 text-white font-medium py-2 px-4 rounded-md">
                        {{ isSubmitting ? 'Submitting...' : 'Submit' }}
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Embedded version - show when embedded is true -->
    <div v-if="show && embedded" class="w-full">
        <div class="py-6">
            <form @submit.prevent="submitForm">
                <div class="grid grid-cols-2 gap-6">
                    <!-- Left Column -->
                    <div class="space-y-4">
                        <div>
                            <label for="assessmentName" class="block font-medium mb-2">Test Name<span
                                    class="text-red-500">*</span></label>
                            <input id="assessmentName" v-model="formData.assessment_name" type="text"
                                class="border border-gray-300 px-3 py-2 rounded-md w-full" required />
                        </div>

                        <!-- Enhanced Search Select for Basis -->
                        <div>
                            <label for="basis" class="block font-medium mb-2">
                                Basis Documents<span class="text-red-500">*</span>
                                <span class="text-xs text-gray-500 ml-1">(Select training materials this assessment is based on)</span>
                            </label>
                            <div class="relative basis-select">
                                <!-- Search Input with Selected Items -->
                                <div
                                    class="relative flex flex-wrap items-center gap-2 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-colors">
                                    <!-- Selected Documents Display -->
                                    <span v-for="docId in formData.basis" :key="docId"
                                        class="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center text-sm font-medium">
                                        <span class="text-xs text-blue-600 mr-2 font-semibold">[{{ getDocumentType(docId)
                                            }}]</span>
                                        {{ getDocumentName(docId) }}
                                        <button type="button" @click.stop="removeBasis(docId)"
                                            class="ml-2 text-blue-600 hover:text-blue-800 focus:outline-none">
                                            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                                            </svg>
                                        </button>
                                    </span>
                                    <!-- Search Input -->
                                    <input type="text" v-model="basisSearch" @input="filterDocuments"
                                        @focus="handleSearchFocus" @keydown.escape="showBasisDropdown = false"
                                        placeholder="Type to search documents..." 
                                        class="flex-grow border-none focus:ring-0 outline-none placeholder-gray-400 text-sm py-1" />
                                </div>

                                <!-- Dropdown Container -->
                                <div v-if="showBasisDropdown"
                                    class="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl">
                                    <!-- Header with search info -->
                                    <div class="px-4 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                                        <div class="flex items-center justify-between">
                                            <span class="text-sm font-medium text-gray-700">Select Documents</span>
                                            <span class="text-xs text-gray-500">{{ filteredDocuments.length }} found</span>
                                        </div>
                                    </div>
                                    
                                    <!-- Type Tabs -->
                                    <div class="flex border-b border-gray-200 bg-gray-50">
                                        <button @click="selectedDocumentType = ''" :class="[
                                            'px-4 py-2 text-sm font-medium transition-colors',
                                            !selectedDocumentType ?
                                                'text-blue-600 border-b-2 border-blue-600 bg-white' :
                                                'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                        ]">
                                            All Types
                                        </button>
                                        <button v-for="type in documentTypes" :key="type"
                                            @click="selectedDocumentType = type" :class="[
                                                'px-4 py-2 text-sm font-medium transition-colors',
                                                selectedDocumentType === type ?
                                                    'text-blue-600 border-b-2 border-blue-600 bg-white' :
                                                    'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                            ]">
                                            {{ type }}
                                        </button>
                                    </div>

                                    <!-- Results Section -->
                                    <div class="max-h-60 overflow-y-auto">
                                        <div v-if="filteredDocuments.length > 0">
                                            <div v-for="doc in filteredDocuments" :key="doc.document_id"
                                                @click="selectBasis(doc)"
                                                class="px-4 py-3 hover:bg-blue-50 cursor-pointer flex items-center border-b border-gray-100 last:border-b-0 transition-colors">
                                                <div class="flex-1">
                                                    <div class="flex items-center">
                                                        <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-3">
                                                            {{ doc.document_type }}
                                                        </span>
                                                        <span class="font-medium text-gray-900">{{ doc.document_name }}</span>
                                                    </div>
                                                    <div class="text-xs text-gray-500 mt-1" v-if="doc.description">
                                                        {{ doc.description.substring(0, 60) }}{{ doc.description.length > 60 ? '...' : '' }}
                                                    </div>
                                                </div>
                                                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                                                </svg>
                                            </div>
                                        </div>
                                        <div v-else class="p-6 text-center">
                                            <svg class="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.709"></path>
                                            </svg>
                                            <p class="text-gray-500 text-sm">No documents found</p>
                                            <p class="text-gray-400 text-xs mt-1">Try adjusting your search or filter</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Enhanced Department Multi-Select -->
                        <div v-if="showDepartmentField" class="mb-4">
                            <label for="department" class="block font-medium mb-2">
                                Departments<span class="text-red-500">*</span>
                                <span class="text-xs text-gray-500 ml-1">(Select which departments this assessment applies to)</span>
                            </label>
                            <div class="relative department-select">
                                <!-- Selected Departments -->
                                <div class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-colors"
                                    @click="toggleDepartmentDropdown">
                                    <div class="flex flex-wrap gap-2">
                                        <span v-for="(department, index) in formData.department_type"
                                            :key="department"
                                            class="bg-green-100 text-green-800 px-3 py-1 rounded-full flex items-center text-sm font-medium">
                                            {{ getDepartmentName(department) }}
                                            <button type="button" @click.stop="removeDepartment(index)"
                                                class="ml-2 text-green-600 hover:text-green-800 focus:outline-none">
                                                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                                                </svg>
                                            </button>
                                        </span>
                                    </div>
                                    <div v-if="formData.department_type.length === 0" class="text-gray-400 text-sm py-1">
                                        Click to select departments
                                    </div>
                                </div>

                                <!-- Enhanced Dropdown -->
                                <div v-if="isDepartmentDropdownVisible"
                                    class="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl">
                                    <!-- Header -->
                                    <div class="px-4 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                                        <div class="flex items-center justify-between">
                                            <span class="text-sm font-medium text-gray-700">Select Departments</span>
                                            <span class="text-xs text-gray-500">{{ departments.length }} available</span>
                                        </div>
                                    </div>
                                    
                                    <!-- Results Section -->
                                    <div class="max-h-60 overflow-y-auto">
                                        <div v-for="department in departments" :key="department.department_id"
                                            @click="toggleDepartment(department.department_id)"
                                            class="px-4 py-3 hover:bg-green-50 cursor-pointer flex items-center border-b border-gray-100 last:border-b-0 transition-colors">
                                            <div class="flex items-center flex-1">
                                                <input type="checkbox" :checked="formData.department_type.includes(department.department_id)"
                                                    class="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 mr-3" />
                                                <div class="flex-1">
                                                    <span class="font-medium text-gray-900">{{ department.name }}</span>
                                                    <div class="text-xs text-gray-500 mt-1" v-if="department.description">
                                                        {{ department.description.substring(0, 50) }}{{ department.description.length > 50 ? '...' : '' }}
                                                    </div>
                                                </div>
                                            </div>
                                            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4"></path>
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Enhanced User Type Multi-Select -->
                        <div v-if="showUserTypeField" class="mb-4">
                            <label for="userType" class="block font-medium mb-2">
                                User Types<span class="text-red-500">*</span>
                                <span class="text-xs text-gray-500 ml-1">(Select which user types can take this assessment)</span>
                            </label>
                            <div class="relative user-type-select">
                                <!-- Selected Roles -->
                                <div class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-colors"
                                    @click="toggleUserTypeDropdown">
                                    <div class="flex flex-wrap gap-2">
                                        <span v-for="(role, index) in formData.user_type" :key="role"
                                            class="bg-purple-100 text-purple-800 px-3 py-1 rounded-full flex items-center text-sm font-medium">
                                            {{ getRoleName(role) }}
                                            <button type="button" @click.stop="removeRole(index)"
                                                class="ml-2 text-purple-600 hover:text-purple-800 focus:outline-none">
                                                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                                                </svg>
                                            </button>
                                        </span>
                                    </div>
                                    <div v-if="formData.user_type.length === 0" class="text-gray-400 text-sm py-1">
                                        Click to select user types
                                    </div>
                                </div>

                                <!-- Enhanced Dropdown -->
                                <div v-if="isUserTypeDropdownVisible"
                                    class="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl">
                                    <!-- Header -->
                                    <div class="px-4 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                                        <div class="flex items-center justify-between">
                                            <span class="text-sm font-medium text-gray-700">Select User Types</span>
                                            <span class="text-xs text-gray-500">{{ roles.length }} available</span>
                                        </div>
                                    </div>
                                    
                                    <!-- Results Section -->
                                    <div class="max-h-60 overflow-y-auto">
                                        <div v-for="role in roles" :key="role.role_id"
                                            @click="toggleRole(role.role_id)"
                                            class="px-4 py-3 hover:bg-purple-50 cursor-pointer flex items-center border-b border-gray-100 last:border-b-0 transition-colors">
                                            <div class="flex items-center flex-1">
                                                <input type="checkbox" :checked="formData.user_type.includes(role.role_id)"
                                                    class="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 mr-3" />
                                                <div class="flex-1">
                                                    <span class="font-medium text-gray-900">{{ role.name }}</span>
                                                    <div class="text-xs text-gray-500 mt-1" v-if="role.description">
                                                        {{ role.description.substring(0, 50) }}{{ role.description.length > 50 ? '...' : '' }}
                                                    </div>
                                                </div>
                                            </div>
                                            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4"></path>
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label for="deadline" class="block font-medium mb-2">Deadline<span
                                    class="text-red-500">*</span></label>
                            <input id="deadline" v-model="formData.deadline" type="date"
                                class="border border-gray-300 px-3 py-2 rounded-md w-full" required />
                        </div>

                        <div>
                            <label for="description" class="block font-medium mb-2">Description<span
                                    class="text-red-500">*</span></label>
                            <textarea id="description" v-model="formData.description" rows="3"
                                class="border border-gray-300 px-3 py-2 rounded-md w-full" required></textarea>
                        </div>
                    </div>

                    <!-- Right Column -->
                    <div class="space-y-4">
                        <div>
                            <label for="numberOfQuestions" class="block font-medium mb-2">Number of Questions<span
                                    class="text-red-500">*</span></label>
                            <input id="numberOfQuestions" v-model="formData.number_of_questions" type="number"
                                class="border border-gray-300 px-3 py-2 rounded-md w-full" required />
                        </div>

                        <div>
                            <label for="passmark" class="block font-medium mb-2">Pass Mark (%)<span
                                    class="text-red-500">*</span></label>
                            <input id="passmark" v-model="formData.passmark" type="number" min="0" max="100"
                                class="border border-gray-300 px-3 py-2 rounded-md w-full" required />
                        </div>

                        <div>
                            <label for="cpdAccredited" class="block font-medium mb-2">CPD Accredited<span
                                    class="text-red-500">*</span></label>
                            <select id="cpdAccredited" v-model="formData.cpd_accredited"
                                class="border border-gray-300 px-3 py-2 rounded-md w-full" required>
                                <option value="">Select</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                            </select>
                        </div>

                        <!-- Certificate expiry period (days) shown only if CPD Accredited is Yes) -->
                        <div v-if="String(formData.cpd_accredited).toLowerCase() === 'yes'">
                            <label class="block font-medium mb-2">Certificate Expiry Period (days)</label>
                            <input v-model.number="formData.certificate_expiry_period" type="number" min="0"
                                class="border border-gray-300 px-3 py-2 rounded-md w-full" placeholder="e.g. 365" />
                        </div>

                        

                        <div>
                            <label class="block font-medium mb-2">Duration<span class="text-red-500">*</span></label>
                            <div class="flex space-x-2">
                                <input v-model="hours" @change="updateDuration" type="number" min="0" max="23" placeholder="Hours"
                                    class="border border-gray-300 px-3 py-2 rounded-md w-1/3" />
                                <input v-model="minutes" @change="updateDuration" type="number" min="0" max="59" placeholder="Minutes"
                                    class="border border-gray-300 px-3 py-2 rounded-md w-1/3" />
                                <input v-model="seconds" @change="updateDuration" type="number" min="0" max="59" placeholder="Seconds"
                                    class="border border-gray-300 px-3 py-2 rounded-md w-1/3" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <div class="border-t border-gray-200 p-4 px-8 mt-6">
                    <div class="flex justify-end">
                        <button type="button" @click="clearForm"
                            class="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-md mr-2">
                            Cancel
                        </button>
                        <button @click="submitForm" :disabled="isSubmitting"
                            class="bg-custom-yellow hover:bg-yellow-500 text-white font-medium py-2 px-4 rounded-md">
                            {{ isSubmitting ? 'Submitting...' : 'Submit' }}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import api from "@/services/api";
import { useToast } from 'vue-toastification';

export default {
    name: 'AssessmentUploadForm',
    props: {
        show: {
            type: Boolean,
            required: true,
        },
        autoClose: {
            type: Boolean,
            default: true,
        },
        embedded: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            toast: useToast(),
            isSubmitting: false,
            errorMessage: '',
            formData: {
                assessment_name: '',
                basis: [],
                department_type: [],
                user_type: [],
                deadline: '',
                description: '',
                number_of_questions: '',
                passmark: '',
                cpd_accredited: '',
                duration: '',
                certificate_expiry_period: null,
                
            },
            documentTypes: ['SOP', 'Training material', 'Manuals', 'Memo'],
            departments: [],
            basisSearch: '',
            documents: [],
            roles: [],
            isDepartmentDropdownVisible: false,
            isUserTypeDropdownVisible: false,
            showDepartmentField: true,
            showUserTypeField: true,
            selectedDocumentType: '',
            showBasisDropdown: false,
            hours: 0,
            minutes: 0,
            seconds: 0,
            error: null
        };
    },
    created() {
        this.fetchRoles();
        this.getDepartments();
        this.fetchDocuments();
        this.updateDuration(); // Initialize duration field
    },
    computed: {
        filteredDocuments() {
            let filtered = this.documents;

            // Filter by selected type
            if (this.selectedDocumentType) {
                filtered = filtered.filter(doc =>
                    doc.document_type === this.selectedDocumentType
                );
            }

            // Filter by search term
            if (this.basisSearch) {
                const searchTerm = this.basisSearch.toLowerCase();
                filtered = filtered.filter(doc =>
                    doc.document_name.toLowerCase().includes(searchTerm)
                );
            }

            // Filter out already selected documents
            return filtered.filter(doc => !this.formData.basis.includes(doc.document_id));
        }
    },

    watch: {
        hours() {
            this.updateDuration();
        },
        minutes() {
            this.updateDuration();
        },
        seconds() {
            this.updateDuration();
        }
    },

    methods: {
        toggleDepartmentDropdown() {
            this.isDepartmentDropdownVisible = !this.isDepartmentDropdownVisible;
        },

        toggleUserTypeDropdown() {
            this.isUserTypeDropdownVisible = !this.isUserTypeDropdownVisible;
        },

        selectDepartment(department) {
            if (!this.formData.department_type.includes(department.department_id)) {
                this.formData.department_type.push(department.department_id);
            }
            this.isDepartmentDropdownVisible = false; // Close dropdown after selection
        },

        selectRole(role) {
            if (!this.formData.user_type.includes(role.role_id)) {
                this.formData.user_type.push(role.role_id);
            }
            this.isUserTypeDropdownVisible = false; // Close dropdown after selection
        },

        removeDepartment(index) {
            this.formData.department_type.splice(index, 1);
        },

        removeRole(index) {
            this.formData.user_type.splice(index, 1);
        },

        getDepartmentName(departmentId) {
            const department = this.departments.find((dept) => dept.department_id === departmentId);
            return department ? department.name : '';
        },

        getRoleName(roleId) {
            const role = this.roles.find((r) => r.role_id === roleId);
            return role ? role.name : '';
        },

        toggleDepartment(departmentId) {
            const index = this.formData.department_type.indexOf(departmentId);
            if (index > -1) {
                this.formData.department_type.splice(index, 1);
            } else {
                this.formData.department_type.push(departmentId);
            }
        },

        toggleRole(roleId) {
            const index = this.formData.user_type.indexOf(roleId);
            if (index > -1) {
                this.formData.user_type.splice(index, 1);
            } else {
                this.formData.user_type.push(roleId);
            }
        },

        updateDuration() {
            this.formData.duration = `${String(this.hours).padStart(2, '0')}:${String(this.minutes).padStart(2, '0')}:${String(this.seconds).padStart(2, '0')}`;
        },

        async getDepartments() {
            try {
                const response = await api.getDepartments();
                this.departments = response.data || response;
            } catch (error) {
                console.error('Error fetching departments:', error);
                this.error = error.message || "Failed to fetch departments";
                this.toast.error("Failed to load departments");
            }
        },

        async fetchDocuments() {
            this.loading = true;
            try {
                const queryParams = { ...this.filters, search_query: this.searchQuery, page: this.currentPage, per_page: this.perPage };
                const response = await api.getDocuments(queryParams);
                this.documents = response.data || response;
                this.currentPage = response.current_page;
                this.totalPages = response.total_pages;
            } catch (error) {
                this.error = error.message || "Failed to fetch basis";
            } finally {
                this.loading = false;
            }
        },

        handleTypeChange() {
            this.basisSearch = ''; // Clear search when type changes
            this.showBasisDropdown = true;
        },

        selectBasis(document) {
            if (!this.formData.basis.includes(document.document_id)) {
                this.formData.basis.push(document.document_id);
            }
            this.basisSearch = '';
            this.showBasisDropdown = false;
        },

        removeBasis(documentId) {
            const index = this.formData.basis.indexOf(documentId);
            if (index > -1) {
                this.formData.basis.splice(index, 1);
            }
        },

        getDocumentName(documentId) {
            const document = this.documents.find(doc => doc.document_id === documentId);
            return document ? document.document_name : '';
        },

        getDocumentType(documentId) {
            const document = this.documents.find(doc => doc.document_id === documentId);
            return document ? document.document_type : '';
        },

        filterDocuments() {
            this.showBasisDropdown = true;
        },

        handleSearchFocus() {
            this.showBasisDropdown = true;
        },

        // Add click outside handler to close dropdowns
        handleClickOutside(event) {
            if (!event.target.closest('.basis-select')) {
                this.showBasisDropdown = false;
            }
            if (!event.target.closest('.department-select')) {
                this.isDepartmentDropdownVisible = false;
            }
            if (!event.target.closest('.user-type-select')) {
                this.isUserTypeDropdownVisible = false;
            }
        },

        async fetchRoles() {
            try {
                const response = await api.getRoles();
                this.roles = (response.data || response).filter(
                    (role) => role.name !== "Super Admin"
                );
            } catch (error) {
                console.error('Error fetching roles:', error);
                this.error = error.message || "Failed to fetch roles";
                this.toast.error("Failed to load user types");
            }
        },
        validateCredentials() {
            const emptyFields = [];
            if (!this.formData.assessment_name) emptyFields.push('Assessment name');
            if (!this.formData.basis) emptyFields.push('Basis');
            if (!this.formData.department_type.length) emptyFields.push('Department type');
            if (!this.formData.user_type.length) emptyFields.push('User type');
            if (!this.formData.deadline) emptyFields.push('Deadline');
            if (!this.formData.description) emptyFields.push('Description');
            if (!this.formData.number_of_questions) emptyFields.push('Number of questions');
            if (!this.formData.passmark) emptyFields.push('Passmark');
            if (!this.formData.cpd_accredited) emptyFields.push('CPD Accredited');
            if (!this.formData.duration) emptyFields.push('Duration');
            return emptyFields;
        },
        async submitForm() {
            // Validate form data
            const emptyFields = this.validateCredentials();
            if (emptyFields.length > 0) {
                this.toast.error("Please fill in all required fields: " + emptyFields.join(', '));
                return;
            }
            if (this.formData.user_type.length === 0) {
                this.toast.error("Please select at least one user type");
                return;
            }

            // Update duration before submitting
            this.updateDuration();
            this.isSubmitting = true;
            try {
                // Convert CPD accredited value to boolean for API
                const formDataToSubmit = {
                    ...this.formData,
                    cpd_accredited: this.convertCpdAccreditedToApiValue(this.formData.cpd_accredited)
                };
                
                console.log('Creating assessment with data:', formDataToSubmit);
                console.log('CPD Accredited value:', this.formData.cpd_accredited);
                
                const response = await api.createAssessment(formDataToSubmit);
                console.log('Assessment creation response:', response);
                this.$emit('assessment-created', response);
                this.toast.success("Assessment created successfully");
                this.clearForm();
                
                if (this.autoClose) {
                    this.closeModal();
                    this.$router.go(0);
                }
            } catch (error) {
                console.error('Error creating assessment:', error);
                const errorMessage = error.response?.data?.message || 'Failed to create assessment. Please try again.';
                this.toast.error(errorMessage);
            } finally {
                this.isSubmitting = false;
            }
        },
        clearForm() {
            this.formData = {
                assessment_name: '',
                basis: '',
                department_type: [],
                user_type: [],
                deadline: '',
                description: '',
                number_of_questions: '',
                passmark: '',
                cpd_accredited: '',
                duration: '',
                certificate_expiry_period: null,
                
            };
            this.selectedDocumentType = '';
            this.basisSearch = '';
            this.showBasisDropdown = false;
            this.hours = 0;
            this.minutes = 0;
            this.seconds = 0;
        },

        closeModal() {
            this.clearForm();
            this.$emit('close-modal');
        },

        // Convert CPD accredited value from form to API value
        convertCpdAccreditedToApiValue(formValue) {
            if (!formValue) {
                return null;
            }
            
            const lowerValue = formValue.toLowerCase();
            if (lowerValue === 'yes') {
                return true; // API expects boolean true
            } else if (lowerValue === 'no') {
                return false; // API expects boolean false
            }
            
            return formValue;
        }
    },

    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },

    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
    }
};
</script>